import { Api } from "@vzbl/integrations";
import { ValueFormatter } from "src/utils/formatters";
import { AggregationConfig, NestedGroupConfig } from "../types";
import { asKey, fromKey } from "src/utils/fields";
import { attributeToTitle, formatValue } from "src/utils/strings";
import { ColumnType } from "antd/lib/table";

// Value Formatting
export const TableFormatter = {
  // Basic value formatting
  formatBasic(value: any): string {
    if (value === null || value === undefined) return "__VZBL::null__";
    if (typeof value === "object") {
      return String(value.value || value.name || value.title || value);
    }
    return String(value);
  },

  // Format with units using column metadata
  formatWithUnit(
    value: number | null,
    property: { target: string; attribute: string } | string,
    columnInfo?: Api.Schema.ColumnInfo
  ): string | number {
    if (value === null) return "__VZBL::null__";

    // Get the field info
    const field = typeof property === "string" ? fromKey(property) : property;
    const fieldKey = asKey(field);

    // Get unit from column info if available
    if (columnInfo) {
      const colInfo = columnInfo.find((c) => c.key === fieldKey);
      if (colInfo?.unit) {
        return ValueFormatter.format(value, {
          field: fieldKey,
          unit: colInfo.unit,
          style: "human",
        });
      }
    }

    // Fall back to just using the field for type detection
    return ValueFormatter.format(value, {
      field: fieldKey,
      style: "human",
    });
  },

  // Format comparison values
  formatComparison(value: string, showAsPercentage: boolean): string | number {
    const [fromStr, toStr] = value.split(",");

    if (fromStr === "__VZBL::null__" || toStr === "__VZBL::null__") {
      return "__VZBL::null__";
    }

    const fromValue = Number(fromStr);
    const toValue = Number(toStr);

    if (!Number.isFinite(fromValue) || !Number.isFinite(toValue)) {
      return "__VZBL::null__";
    }

    if (fromValue === 0) {
      if (toValue === 0) return "__VZBL::null__";
      return showAsPercentage ? 100 : toValue;
    }

    const change = showAsPercentage
      ? ((toValue - fromValue) / fromValue) * 100
      : toValue - fromValue;

    return Number.isFinite(change) ? change : "__VZBL::null__";
  },

  // Format aggregation values
  formatAggregation(
    value: number | null | string,
    aggregation: AggregationConfig,
    config?: NestedGroupConfig,
    columnInfo?: Api.Schema.ColumnInfo
  ): string | number {
    if (value === null || value === "__VZBL::null__") {
      return "__VZBL::null__";
    }

    if (
      typeof value === "string" &&
      value.includes(",") &&
      config?.showChangeAs
    ) {
      return this.formatComparison(value, config.showChangeAs === "percentage");
    }

    // Default to basic unit formatting
    return this.formatWithUnit(
      typeof value === "number" ? value : null,
      aggregation.key,
      columnInfo
    );
  },

  formatAggregationTitle(aggregation: AggregationConfig): string {
    // First try to detect if this is a field with a special unit type
    const valueType = ValueFormatter.detectValueType({
      field: aggregation.field,
    });
    if (valueType) {
      const value = Number.parseInt(aggregation.key, 10);
      if (!Number.isNaN(value)) {
        return ValueFormatter.format(value, {
          type: valueType,
          field: aggregation.field,
        });
      }
    }

    // Get reducer name and capitalize first letter
    const reducerName = aggregation.reducer
      ? aggregation.reducer.charAt(0).toUpperCase() +
        aggregation.reducer.slice(1)
      : "";

    // Extract field name from the key, removing any table prefix and function wrapper
    const fieldName = aggregation.field.split(".").pop() || aggregation.field;

    // Remove any function wrapper if present (e.g., "avg(PullRequest.cycleTime)" -> "cycleTime")
    const cleanField = fieldName.includes("(")
      ? fieldName.slice(fieldName.indexOf("(") + 1).replace(/\)$/, "")
      : fieldName;

    // Convert camelCase to Title Case (e.g., cycleTime -> Cycle Time)
    const formattedField = attributeToTitle(cleanField);

    // Special cases for common reducers
    switch (aggregation.reducer) {
      case "avg": {
        return `Avg ${formattedField}`;
      }
      case "count": {
        return `# of ${formattedField}`;
      }
      default: {
        return `${reducerName} ${formattedField}`;
      }
    }
  },

  formatDimension(
    value: any,
    dimensionKey: string,
    columnInfo?: Api.Schema.ColumnInfo
  ): any {
    // First, try to find column info to get unit information
    if (columnInfo) {
      const colInfo = columnInfo.find((c) => c.key === dimensionKey);
      if (colInfo?.unit) {
        // Use formatValue which handles ww/yy to mm/dd/yy transformation for yearweek units
        return formatValue(value, colInfo.type, colInfo.unit);
      }
    }

    const detectedType = ValueFormatter.detectValueType({
      field: dimensionKey,
    });

    if (!detectedType) {
      return value;
    }

    if (typeof value === "object" || value === null || value === undefined) {
      return value;
    }

    const numValue = TableValidators.toNumber(value);
    if (numValue === null) {
      return value;
    }
    //TODO currently doesn't handle weeks like ww/yy... need to add that in the format
    return ValueFormatter.format(numValue, {
      type: detectedType,
      style: "human",
    });
  },
};

// Value Validation
export const TableValidators = {
  isEmpty(value: any): boolean {
    return (
      value === null ||
      value === undefined ||
      value === "__VZBL::null__" ||
      value === ""
    );
  },

  isComparison(value: any): value is any {
    return (
      typeof value === "object" &&
      value !== null &&
      Array.isArray((value as any).tuple) &&
      (value as any).tuple.length === 2
    );
  },

  toNumber(value: any): number | null {
    if (this.isEmpty(value)) return null;

    if (typeof value === "object" && value !== null) {
      if (this.isComparison(value)) {
        const num = Number(value.tuple[1]);
        return Number.isFinite(num) ? num : null;
      }
      const num = Number((value as any).value);
      return Number.isFinite(num) ? num : null;
    }

    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  },
};

export function mapFields(
  dimensions: { title: string; key: string }[],
  groupBys: any[]
): Array<{
  target: string;
  attribute: string;
  key: string;
}> {
  return dimensions.map((dimension, idx) => {
    const groupBy = groupBys[idx];
    return {
      target: groupBy?.target || "Event",
      attribute: groupBy?.attribute || dimension.title,
      key: dimension.key,
    };
  });
}
// Helper functions
export function isEmptyValue(value: any): boolean {
  return (
    value === null ||
    value === undefined ||
    value === "__VZBL::null__" ||
    value === ""
  );
}

export function filterTableColumns<T>(
  columns: ColumnType<T>[],
  configColumns: string[]
): ColumnType<T>[] {
  return columns.filter((col) => {
    const dataIndexStr = `${col.dataIndex}`;
    const keyStr = `${col.key}`;

    // Check if any config column starts with our dataIndex or key
    // This handles both filtered and unfiltered versions
    const matchingConfigColumn = configColumns.find(
      (configCol) =>
        configCol.startsWith(dataIndexStr) || configCol.startsWith(keyStr)
    );

    return configColumns.length === 0 || Boolean(matchingConfigColumn);
  });
}
